import React, {useEffect, useState} from 'react';
import {generateMicrolessonsText} from '../generateMicrolessonsText';
import PageTitle from '../../../../uiToolkit/PageTitle';
import './styles.css';
import BackButton from '../../../../uiToolkit/Buttons/BackButton';
import TextAreaInput from '../../../../uiToolkit/Inputs/TextAreaInput';
import ToggleSwitch from '../../../../uiToolkit/Inputs/ToggleSwitch';
import {ASSESMENT_OPTIONS, BRANCHING_OPTIONS, SOURCE_TYPE} from '../conf';
import NumbersInput from '../../../../uiToolkit/Inputs/NumbersInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import {LessonGenerationFromPromptModel} from '../../../../data/models/AI/LessonGenerationModel';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import LibraryFilesInput from '../../../../uiToolkit/Inputs/LibraryFilesInput';
import Dropdown from '../../../../uiToolkit/Inputs/Dropdown';
import {
  DIALECTS,
  LANGUAGE,
} from '../../../../data/models/AI/VideoGenerationOptions';
import TextInput from '../../../../uiToolkit/Inputs/TextInput';
import {generateAIVideosText} from '../../GenerateAIVideo/generateAIVideosText';
import {numberWithCommas} from '../../../../utils/getFirstCapitals';
import {Tour} from '../../../../modules/Tour';
import featureFlags, {FeatureFlagName} from '../../../../utils/featureFlags';
import Tooltip2 from '../../../../uiToolkit/Tooltip2/Tooltip2';
import ErrorBox2 from '../../../../uiToolkit/ErrorBox2/ErrorBox2';
import {internalErrorCodes} from '../../../../data/errorCodes';

interface Props {
  onCancel: () => void;
  isLoading: boolean;
  isEstimationLoading: boolean;
  estimatedCost: number;
  availableCredits: number;
  calculateEstimation: (prompt: string, language: string) => void;
  onGenerateFromPrompt: (model: LessonGenerationFromPromptModel) => void;
  onOpenLibrary: (
    fileType: 'video' | 'document',
    selectedFile?: MediaFile,
  ) => Promise<MediaFile>;
  onGenerateFromVideo: (
    includeBranch: boolean,
    includeGrading: boolean,
    contentId: string,
    language: string,
    dialect: string,
    name: string,
    passingScore?: number,
    mediaType?: 'video' | 'document',
  ) => void;
  isGenerationAvailable: boolean;
}

const GenerateMicrolessonUI = React.memo(
  ({
    onCancel,
    isLoading,
    isEstimationLoading,
    estimatedCost,
    availableCredits,
    calculateEstimation,
    onGenerateFromPrompt,
    onOpenLibrary,
    onGenerateFromVideo,
    isGenerationAvailable,
  }: Props) => {
    const [prompt, setPrompt] = useState('');
    const [includeBranching, setIncludeBranching] = useState('1');
    const [includeGrading, setIncludeGrading] = useState('1');
    const [passingScore, setPassingScore] = useState<number | undefined>(50);
    const [source, setSource] = useState('1');
    const [videoFile, setVideoFile] = useState<MediaFile>();
    const [documentFile, setDocumentFile] = useState<MediaFile>();
    const [language, setLanguage] = useState('en');
    const initialDialects = DIALECTS[language as keyof typeof DIALECTS].map(
      (dialect: any) => ({key: dialect, value: dialect}),
    );
    const [dialectOptions, setDialectOptions] =
      useState<{key: string; value: string}[]>(initialDialects);
    const [selectedDialect, setSelectedDialect] = useState<string>(
      initialDialects[0]?.key,
    );
    const [title, setTitle] = useState('');

    const [lngDropdownIsOpen, setLngDropdownIsOpen] = useState(false);
    const [dialectDropdownIsOpen, setDialectDropdownIsOpen] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
      calculateEstimation(prompt, language);
    }, [prompt, language]);

    useEffect(() => {
      setPrompt('');
      setVideoFile(undefined);
      setDocumentFile(undefined);
    }, [source]);

    const saveEnabled = () => {
      if (!title) return false;

      if (source === '1') {
        return (
          prompt !== '' &&
          selectedDialect !== '' &&
          availableCredits > estimatedCost
        );
      }

      if (source === '2') {
        return documentFile !== undefined;
      }

      if (source === '3') {
        return videoFile !== undefined;
      }

      return false;
    };

    const updateDialectOptions = (language: string) => {
      const dialects = DIALECTS[language as keyof typeof DIALECTS];
      const options = dialects.map((dialect: any) => ({
        key: dialect,
        value: dialect,
      }));
      setDialectOptions(options);
      setSelectedDialect(options[0].key);
    };

    const onLanguageSelect = (value: string) => {
      setLanguage(value);
      setSelectedDialect('');
      updateDialectOptions(value);
    };

    const showLibrary = async () => {
      const file = await onOpenLibrary(source === '2' ? 'document' : 'video');
      if (!file) return;

      if (source === '2') {
        setDocumentFile(file);
      } else if (source === '3') {
        setVideoFile(file);
      }
    };

    const handleSave = () => {
      if (!isGenerationAvailable) {
        setErrors([internalErrorCodes.AI_GENERATION_NOT_AVAILABLE]);
        return;
      }
      if (source === '1') {
        onGenerateFromPrompt({
          prompt,
          addBranching: includeBranching === '1',
          addAssessment: includeGrading === '1',
          passingScore,
          language: language,
          dialect: selectedDialect,
          name: title,
        });
      } else if (source === '2') {
        onGenerateFromVideo(
          includeBranching === '1',
          includeGrading === '1',
          documentFile?.id || '',
          language,
          selectedDialect,
          title,
          passingScore,
          'document',
        );
      } else if (source === '3') {
        onGenerateFromVideo(
          includeBranching === '1',
          includeGrading === '1',
          videoFile?.storageFileName || '',
          language,
          selectedDialect,
          title,
          passingScore,
          'video',
        );
      }
    };

    const isLessonFromDocumentFeatureEnabled = featureFlags.get(
      FeatureFlagName.LessonFromDocument,
    );

    return (
      <div className="GenerateMicrolessonsContainer">
        {source === '1' && <Tour section="microlessons" />}
        <PageTitle
          title={generateMicrolessonsText.title}
          subtitle={generateMicrolessonsText.subtitle}
        />
        <div className="TitleContainer">
          <BackButton />
          <h2>{generateMicrolessonsText.generateTitle}</h2>
          <div />
        </div>
        <div className="GenerateMicrolessonBody">
          <div className="GenerateMicrolessonCard">
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.source}
              <span className="MandatoryIndicator">*</span>
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.sourceSubtitle}
            </p>
            <ToggleSwitch
              options={
                isLessonFromDocumentFeatureEnabled
                  ? SOURCE_TYPE
                  : SOURCE_TYPE.filter(x => x.key !== '2')
              }
              selectedKey={source}
              onChange={setSource}
            />
            {source === '1' && (
              <>
                <p className="LessonGenVideoTitle">
                  {generateMicrolessonsText.promptTitle}
                </p>
                <span className="PromptInfo">
                  {generateMicrolessonsText.promptSubtitle}
                </span>
                <TextAreaInput
                  placeholder={generateMicrolessonsText.promptPlaceholder}
                  value={prompt}
                  onChange={setPrompt}
                  maxCharacters={250}
                />
              </>
            )}
            {source === '2' && (
              <>
                <p className="LessonGenVideoTitle">
                  <Tooltip2
                    showIcon={true}
                    text={generateMicrolessonsText.documentRequirements}>
                    {generateMicrolessonsText.documentTitle}
                  </Tooltip2>
                </p>
                <LibraryFilesInput
                  onBrowse={showLibrary}
                  files={documentFile ? [documentFile] : []}
                  onRemoveFile={() => setDocumentFile(undefined)}
                  singleFile
                />
              </>
            )}
            {source === '3' && (
              <>
                <p className="LessonGenVideoTitle">
                  {generateMicrolessonsText.videoTitle}
                </p>
                <LibraryFilesInput
                  onBrowse={showLibrary}
                  files={videoFile ? [videoFile] : []}
                  onRemoveFile={() => setVideoFile(undefined)}
                  singleFile
                />
              </>
            )}

            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.titleField}
              <span className="MandatoryIndicator">*</span>
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.titleDescription}
            </p>
            <TextInput
              value={title}
              placeholder={generateMicrolessonsText.titleField}
              onChange={setTitle}
            />

            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.includeBranching}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.branchingSubtitle}
            </p>
            <ToggleSwitch
              options={BRANCHING_OPTIONS}
              selectedKey={includeBranching}
              onChange={setIncludeBranching}
            />
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.includeGrading}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.assesmentSubtitle}
            </p>
            <ToggleSwitch
              options={ASSESMENT_OPTIONS}
              selectedKey={includeGrading}
              onChange={setIncludeGrading}
            />
            {includeGrading === '1' && (
              <>
                <p className="LessonGenVideoTitle">
                  {generateMicrolessonsText.passingScore}
                </p>
                <p className="LessonGenVideoSubTitle">
                  {generateMicrolessonsText.passingScoreSubtitle}
                </p>
                <NumbersInput
                  value={passingScore}
                  setValue={setPassingScore}
                  disabled={!includeGrading}
                  className="LessonGenPassingScore"
                />
              </>
            )}
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.selectLanguage}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.selectLanguageSubtitle}
            </p>
            <div className="DropdownGroup">
              <Dropdown
                options={LANGUAGE}
                selectedOption={language}
                setSelectedOption={onLanguageSelect}
                className="LanguageDropdown"
                onDropdownVisibilityToggle={setLngDropdownIsOpen}
              />
              <Dropdown
                options={dialectOptions}
                selectedOption={selectedDialect}
                setSelectedOption={setSelectedDialect}
                className="LanguageDropdown"
                onDropdownVisibilityToggle={setDialectDropdownIsOpen}
              />
            </div>
            {source === '1' && (
              <div className="Estimation">
                <ul>
                  <li>
                    {generateAIVideosText.summaryAvailableCredits}
                    <span
                      className={`CreditsCost ${
                        availableCredits < estimatedCost ? 'CreditError' : ''
                      }`}>
                      {numberWithCommas(availableCredits.toString())}
                    </span>
                  </li>
                  <li>
                    {generateAIVideosText.summaryEstimatedCost}
                    <span className="CreditsCost">
                      {isEstimationLoading
                        ? generateAIVideosText.calculating
                        : numberWithCommas(estimatedCost.toString())}
                    </span>
                  </li>
                </ul>
              </div>
            )}
            <ErrorBox2
              errorCodes={errors}
              clearErrors={() => setErrors([])}
              className="GenerateMicrolessonErrorBox"
              uiType="standard"
            />
            <div className="Footer">
              <Button
                title={generateMicrolessonsText.cancel}
                onClick={onCancel}
                uiType="text"
              />
              <Button
                title={generateMicrolessonsText.generate}
                onClick={handleSave}
                uiType="action"
                isLoading={isLoading}
                disabled={!saveEnabled()}
              />
            </div>
            {(lngDropdownIsOpen || dialectDropdownIsOpen) && (
              <div className="Spacer" />
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default GenerateMicrolessonUI;
